<template>
  <div>
    <!-- Header Bar -->
    <div
      class="d-flex align-items-center justify-content-between mb-1 flex-wrap"
    >
      <div class="d-flex align-items-center mb-50 mb-sm-0">
        <b-button
          variant="outline-primary"
          size="sm"
          class="mr-1 shadow-sm"
          @click="$emit('back')"
        >
          <feather-icon icon="ArrowLeftIcon" size="15" class="mr-50" />
          {{ $t("Back") }}
        </b-button>
        <div class="d-flex align-items-center">
          <feather-icon
            icon="CalendarIcon"
            size="20"
            class="text-primary mr-50"
          />
          <h4 class="mb-0 font-weight-bolder">{{ $t("Manage Datesheets") }}</h4>
          <b-badge
            v-if="sheetItems && sheetItems.length"
            variant="light-primary"
            pill
            class="ml-1 px-75 py-25 font-small-2"
          >
            {{ sheetItems.length }} {{ $t("Records") }}
          </b-badge>
        </div>
      </div>

      <!-- Action Buttons -->
      <div>
        <template v-if="!isEditMode">
          <b-button
            v-if="sheetItems && sheetItems.length"
            variant="primary"
            size="sm"
            class="shadow-sm"
            @click="toggleEditMode(true)"
            :disabled="dataLoading"
          >
            <feather-icon icon="EditIcon" size="14" class="mr-50" />
            {{ $t("Edit") }}
          </b-button>
        </template>
        <template v-else>
          <b-button
            variant="success"
            size="sm"
            class="mr-1 shadow-sm"
            @click="SaveData"
            :disabled="savingData"
          >
            <b-spinner v-if="savingData" small class="mr-50" />
            <feather-icon v-else icon="CheckIcon" size="14" class="mr-50" />
            {{ $t("Save") }}
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="toggleEditMode(false)"
            :disabled="savingData"
          >
            <feather-icon icon="XIcon" size="14" class="mr-50" />
            {{ $t("Cancel") }}
          </b-button>
        </template>
      </div>
    </div>

    <!-- Main Card -->
    <b-card no-body class="card-action shadow-sm border-0">
      <!-- Filter Bar -->
      <div class="p-1 border-bottom bg-light-primary-subtle">
        <b-row class="align-items-center">
          <b-col md="4" lg="3" sm="6" cols="12">
            <b-form-group :label="$t('Session')" class="mb-0">
              <v-select
                v-model="filterSessionID"
                :options="sessionOptions"
                :reduce="(val) => val.id"
                label="session"
                :clearable="false"
                :disabled="isEditMode || dataLoading"
                :placeholder="$t('Select Session')"
                @input="onSessionSelect"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Table Section -->
      <div class="px-1 py-50">
        <b-table
          responsive
          hover
          :items="sheetItems"
          :fields="fields"
          :busy="dataLoading"
          class="custom-datesheet-table align-middle mb-0"
          show-empty
          :empty-text="$t('No Data Found')"
        >
          <!-- Busy state -->
          <template #table-busy>
            <div class="text-center text-primary my-3">
              <b-spinner class="align-middle mr-1" />
              <strong>{{ $t("Loading Datesheets...") }}</strong>
            </div>
          </template>

          <!-- Status -->
          <template #cell(status)="data">
            <b-badge
              :variant="
                data.item.datesheetGrid && data.item.datesheetGrid.isPublished
                  ? 'light-success'
                  : 'light-secondary'
              "
              pill
              class="px-75 py-25"
            >
              {{
                data.item.datesheetGrid && data.item.datesheetGrid.isPublished
                  ? $t("Published")
                  : $t("Unpublished")
              }}
            </b-badge>
          </template>

          <!-- Test Day & Date -->
          <template #cell(testDayDate)="data">
            <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 160px">
                <flat-pickr
                  v-model="data.item.datesheetData.date"
                  :config="dateConfig"
                  class="form-control form-control-sm"
                  :placeholder="$t('Select date')"
                  @on-change="onDateChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template>
            <template v-else>
              <span
                v-if="data.item.datesheetData"
                class="font-weight-bold text-dark"
              >
                {{ formatTestDayDate(data.item.datesheetData) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </template>

          <!-- Exam -->
          <template #cell(exam)="data">
            <b-badge
              v-if="data.item.metaGrid && data.item.metaGrid.exam"
              variant="light-primary"
              class="font-weight-medium px-75 py-25"
            >
              <feather-icon icon="AwardIcon" size="12" class="mr-25" />
              {{ data.item.metaGrid.exam }}
            </b-badge>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Class -->
          <template #cell(class)="data">
            <b-badge
              v-if="data.item.metaGrid && data.item.metaGrid.cls"
              variant="light-dark"
              class="font-weight-bolder px-75 py-25"
            >
              {{ data.item.metaGrid.cls }}
            </b-badge>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Section -->
          <template #cell(section)="data">
            <b-badge
              v-if="data.item.metaGrid && data.item.metaGrid.sec"
              variant="light-dark"
              class="font-weight-bolder px-75 py-25"
            >
              {{ data.item.metaGrid.sec }}
            </b-badge>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Subject -->
          <template #cell(subject)="data">
            <span
              v-if="data.item.metaGrid && data.item.metaGrid.subject"
              class="font-weight-medium text-dark"
            >
              {{ data.item.metaGrid.subject }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Syllabus -->
          <template #cell(syllabus)="data">
            <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 220px">
                <div
                  class="d-flex align-items-center justify-content-between mb-25"
                >
                  <span class="small text-muted">{{ $t("Urdu") }}</span>
                  <b-form-checkbox
                    v-model="data.item.datesheetData.isOther"
                    switch
                    class="ml-1"
                    v-b-tooltip.hover.top
                    :title="$t('Urdu')"
                    @change="onSyllabusChange(data.item.datesheetData)"
                  />
                </div>
                <b-form-textarea
                  v-model="data.item.datesheetData.syllabus"
                  size="sm"
                  rows="2"
                  :placeholder="$t('Enter syllabus')"
                  :class="{ 'text-right': data.item.datesheetData.isOther }"
                  @input="onSyllabusChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template>
            <template v-else>
              <span
                v-if="
                  data.item.datesheetData && data.item.datesheetData.syllabus
                "
                class="text-secondary small font-weight-normal text-wrap d-block"
                :class="{ 'text-right': data.item.datesheetData.isOther }"
              >
                {{ data.item.datesheetData.syllabus }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </template>

          <!-- Delivered Date -->
          <template #cell(deliveredDate)="data">
            <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 140px">
                <flat-pickr
                  v-model="getMetaData(data.item.datesheetData).deliveredDate"
                  :config="dateConfig"
                  class="form-control form-control-sm"
                  :placeholder="$t('Select date')"
                  @on-change="onMetaChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template>
            <template v-else>
              <span
                v-if="
                  data.item.datesheetData &&
                  getMetaData(data.item.datesheetData).deliveredDate
                "
                class="text-dark"
              >
                {{
                  formatSimpleDate(
                    getMetaData(data.item.datesheetData).deliveredDate
                  )
                }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </template>

          <!-- Checked Date -->
          <template #cell(checkedDate)="data">
            <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 140px">
                <flat-pickr
                  v-model="getMetaData(data.item.datesheetData).checkedDate"
                  :config="dateConfig"
                  class="form-control form-control-sm"
                  :placeholder="$t('Select date')"
                  @on-change="onMetaChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template>
            <template v-else>
              <span
                v-if="
                  data.item.datesheetData &&
                  getMetaData(data.item.datesheetData).checkedDate
                "
                class="text-dark"
              >
                {{
                  formatSimpleDate(
                    getMetaData(data.item.datesheetData).checkedDate
                  )
                }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </template>

          <!-- Distribution Date -->
          <template #cell(distributionDate)="data">
            <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 140px">
                <flat-pickr
                  v-model="
                    getMetaData(data.item.datesheetData).distributionDate
                  "
                  :config="dateConfig"
                  class="form-control form-control-sm"
                  :placeholder="$t('Select date')"
                  @on-change="onMetaChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template>
            <template v-else>
              <span
                v-if="
                  data.item.datesheetData &&
                  getMetaData(data.item.datesheetData).distributionDate
                "
                class="text-dark"
              >
                {{
                  formatSimpleDate(
                    getMetaData(data.item.datesheetData).distributionDate
                  )
                }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </template>

          <!-- Send to IT -->
          <template #cell(sendtoIT)="data">
            <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 130px">
                <v-select
                  v-model="getMetaData(data.item.datesheetData).sendtoIT"
                  :options="metaStatusOptions"
                  :clearable="true"
                  :placeholder="$t('Select')"
                  @input="onMetaChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template>
            <template v-else>
              <template
                v-if="
                  data.item.datesheetData &&
                  getMetaData(data.item.datesheetData).sendtoIT
                "
              >
                <b-badge
                  :variant="
                    getMetaData(
                      data.item.datesheetData
                    ).sendtoIT.toLowerCase() === 'done'
                      ? 'light-success'
                      : 'light-warning'
                  "
                  pill
                  class="px-75 py-25 text-capitalize"
                >
                  {{ $t(getMetaData(data.item.datesheetData).sendtoIT) }}
                </b-badge>
              </template>
              <span v-else class="text-muted">-</span>
            </template>
          </template>

          <!-- Result Publication -->
          <template #cell(resultPublication)="data">
            <!-- <template v-if="isEditMode">
              <div v-if="data.item.datesheetData" style="min-width: 130px;">
                <v-select
                  v-model="getMetaData(data.item.datesheetData).resultPublication"
                  :options="metaStatusOptions"
                  :clearable="true"
                  :placeholder="$t('Select')"
                  @input="onMetaChange(data.item.datesheetData)"
                />
              </div>
              <span v-else class="text-muted">-</span>
            </template> -->
            <template>
              <template
                v-if="
                  data.item.datesheetData &&
                  getMetaData(data.item.datesheetData).resultPublication
                "
              >
                <b-badge
                  :variant="
                    getMetaData(
                      data.item.datesheetData
                    ).resultPublication.toLowerCase() === 'done'
                      ? 'light-success'
                      : 'light-warning'
                  "
                  pill
                  class="px-75 py-25 text-capitalize"
                >
                  {{
                    $t(getMetaData(data.item.datesheetData).resultPublication)
                  }}
                </b-badge>
              </template>
              <span v-else class="text-muted">-</span>
            </template>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  VBTooltip,
  BFormGroup,
  BForm,
  BSpinner,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BFormTextarea,
  BInputGroup,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { quillEditor } from "vue-quill-editor";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    BFormTextarea,
    BFormCheckbox,
    BCard,
    BRow,
    BBadge,
    BTable,
    BCol,
    BFormInput,
    BButton,
    BSidebar,
    BFormGroup,
    BInputGroupPrepend,
    BInputGroup,
    BForm,
    BPagination,
    BSpinner,
    BDropdown,
    BDropdownItem,
    flatPickr,
    vSelect,
    quillEditor,
    ToastificationContent,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
  },
  props: {
    sessionOptions: {
      type: Array,
      default: () => [],
    },
  },
  async created() {},
  computed: {},
  data() {
    return {
      filterSessionID: null,
      dataLoading: false,
      savingData: false,
      isEditMode: false,
      originalSheetItems: [],
      metaStatusOptions: ["done", "pending"],
      dateConfig: {
        altInput: true,
        altFormat: "j M, Y",
        dateFormat: "Y-m-d",
      },
      days: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      sheetItems: [],
      fields: [
        { key: "status", label: "App Status" },
        { key: "testDayDate", label: "Test Date" },
        { key: "exam", label: "Exam" },
        { key: "class", label: "Class" },
        { key: "section", label: "Section" },
        { key: "subject", label: "Subject" },
        { key: "syllabus", label: "Syllabus" },
        { key: "deliveredDate", label: "Delivered Date" },
        { key: "checkedDate", label: "Checked Date" },
        { key: "distributionDate", label: "Distribution Date" },
        { key: "sendtoIT", label: "Send to IT" },
        { key: "resultPublication", label: "Result Publication" },
      ],
    };
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getMetaData(datesheetData) {
      if (!datesheetData) return {};
      if (!datesheetData._meta) {
        let parsed = {};
        if (datesheetData.meta_data) {
          if (typeof datesheetData.meta_data === "string") {
            try {
              parsed = JSON.parse(datesheetData.meta_data);
            } catch (e) {
              parsed = {};
            }
          } else if (typeof datesheetData.meta_data === "object") {
            parsed = { ...datesheetData.meta_data };
          }
        }
        this.$set(datesheetData, "_meta", {
          deliveredDate: parsed.deliveredDate || null,
          checkedDate: parsed.checkedDate || null,
          distributionDate: parsed.distributionDate || null,
          sendtoIT: parsed.sendtoIT || null,
          resultPublication: parsed.resultPublication || null,
        });
      }
      return datesheetData._meta;
    },

    onMetaChange(datesheetData) {
      if (datesheetData) {
        datesheetData._isEdited = true;
        if (datesheetData._meta) {
          datesheetData.meta_data = JSON.stringify(datesheetData._meta);
        }
      }
    },

    formatSimpleDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(d);
    },

    hasUnsavedChanges() {
      if (
        !this.sheetItems ||
        !this.sheetItems.length ||
        !this.originalSheetItems ||
        !this.originalSheetItems.length
      ) {
        return false;
      }
      return this.sheetItems.some((item, index) => {
        const currData = item.datesheetData;
        if (!currData) return false;
        const origData = this.originalSheetItems[index]?.datesheetData;
        if (!origData) return true;

        const currMetaStr = currData._meta
          ? JSON.stringify(currData._meta)
          : currData.meta_data || "";
        const origMetaStr = origData._meta
          ? JSON.stringify(origData._meta)
          : origData.meta_data || "";

        return (
          currData._isEdited ||
          currData.date !== origData.date ||
          currData.syllabus !== origData.syllabus ||
          currData.isOther !== origData.isOther ||
          currMetaStr !== origMetaStr
        );
      });
    },

    async confirmDiscardChanges() {
      const res = await Swal.fire({
        title: this.$t("Unsaved Changes"),
        text: this.$t(
          "Are you sure you want to cancel? Any changes made will be discarded."
        ),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ea5455",
        cancelButtonColor: "#82868b",
        confirmButtonText: this.$t("Yes, discard changes"),
        cancelButtonText: this.$t("Continue editing"),
      });
      return res.isConfirmed;
    },

    async toggleEditMode(enable) {
      if (enable) {
        this.originalSheetItems = JSON.parse(JSON.stringify(this.sheetItems));
        this.isEditMode = true;
      } else {
        if (this.hasUnsavedChanges()) {
          const confirmed = await this.confirmDiscardChanges();
          if (!confirmed) {
            return;
          }
        }
        if (this.originalSheetItems && this.originalSheetItems.length) {
          this.sheetItems = JSON.parse(JSON.stringify(this.originalSheetItems));
          this.sheetItems.forEach((item) => {
            if (item && item.datesheetData) {
              this.getMetaData(item.datesheetData);
            }
          });
        }
        this.isEditMode = false;
      }
    },

    onSessionSelect() {
      if (this.isEditMode) {
        this.isEditMode = false;
      }
      this.LoadData();
    },

    onDateChange(datesheetData) {
      if (datesheetData && datesheetData.date) {
        let d = new Date(datesheetData.date);
        if (!isNaN(d.getTime())) {
          datesheetData.day = this.days[d.getDay()];
        }
      }
      if (datesheetData) {
        datesheetData._isEdited = true;
      }
    },

    onSyllabusChange(datesheetData) {
      if (datesheetData) {
        datesheetData._isEdited = true;
      }
    },

    formatTestDayDate(datesheetData) {
      if (!datesheetData) return "";
      if (datesheetData.date) {
        const d = new Date(datesheetData.date);
        if (!isNaN(d.getTime())) {
          const dayName =
            datesheetData.day?.substring(0, 3) ||
            new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(d);
          const dateStr = new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }).format(d);
          return `${dayName}, ${dateStr}`;
        }
      }
      return datesheetData.day || "";
    },

    async SaveData() {
      if (!this.sheetItems || !this.sheetItems.length) return;

      const editedList = [];
      this.sheetItems.forEach((item, index) => {
        const currData = item.datesheetData;
        if (!currData) return;

        const origData = this.originalSheetItems[index]?.datesheetData;
        const currMetaStr = currData._meta
          ? JSON.stringify(currData._meta)
          : currData.meta_data || "";
        const origMetaStr = origData._meta
          ? JSON.stringify(origData._meta)
          : origData.meta_data || "";

        const isEdited =
          currData._isEdited ||
          !origData ||
          currData.date !== origData.date ||
          currData.syllabus !== origData.syllabus ||
          currData.isOther !== origData.isOther ||
          currMetaStr !== origMetaStr;

        if (isEdited) {
          const copy = { ...currData };
          if (copy._meta) {
            copy.meta_data = JSON.stringify(copy._meta);
          }
          delete copy._isEdited;
          delete copy._meta;
          editedList.push(copy);
        }
      });

      if (editedList.length === 0) {
        if (this.$toast) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("Information"),
              icon: "InfoIcon",
              variant: "info",
              text: this.$t("No changes detected to save."),
            },
          });
        }
        this.isEditMode = false;
        return;
      }

      this.savingData = true;
      var status = await this.post({
        url:
          this.$store.state.domain +
          "DatesheetGrid/SaveSheetData?db=" +
          this.$store.state.userData.db,
        body: editedList,
        message: this.$t("Datesheet data saved successfully!"),
        context: this,
        token: this.$store.state.userData.token,
      });
      this.savingData = false;

      if (status) {
        this.isEditMode = false;
        await this.LoadData();
      }
    },

    async LoadData() {
      if (!this.filterSessionID) return;
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "DatesheetGrid/LoadBySession?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&sessionID=" +
          this.filterSessionID,
        token: this.$store.state.userData.token,
      };
      this.sheetItems = await this.get(obj);
      if (this.sheetItems && Array.isArray(this.sheetItems)) {
        this.sheetItems.forEach((item) => {
          if (item && item.datesheetData) {
            this.getMetaData(item.datesheetData);
          }
        });
      }
      console.log(this.sheetItems);
      this.dataLoading = false;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>

<style scoped>
.custom-datesheet-table ::v-deep th {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  background-color: #f8f8f8;
  color: #5e5873;
  font-weight: 600;
  border-top: none;
}
.custom-datesheet-table ::v-deep td {
  vertical-align: middle;
  padding: 0.85rem 1rem;
}
</style>
